import React, { useEffect, useMemo, useRef } from 'react';
import { FunnelGraph } from '../../vendor/funnel-graph-js';
import '../../vendor/funnel-graph-js/funnel-graph.css';

type FunnelMode = 'light' | 'dark';

/** Pinned in funnelchart.theme.test.tsx — mirrors --funnel-grad-1/-2 in index.css. */
const FUNNEL_GRADIENT: Record<FunnelMode, [string, string]> = {
    light: ['#8D5DD9', '#5A9CFF'],
    dark: ['#8D5DD9', '#4A8CF5'],
};

function domMode(): FunnelMode {
    return typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
        ? 'dark'
        : 'light';
}

function cssVar(name: string, fallback: string): string {
    if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') return fallback;
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
}

function defaultGradient(): [string, string] {
    const [fallback1, fallback2] = FUNNEL_GRADIENT[domMode()];
    return [cssVar('--funnel-grad-1', fallback1), cssVar('--funnel-grad-2', fallback2)];
}

export interface FunnelChartProps {
    labels: string[];
    values: number[];
    /** Two-stop gradient; defaults to the `--funnel-grad-1`/`--funnel-grad-2` tokens for the current theme. */
    colors?: [string, string];
    displayPercent?: boolean;
    height?: number;
    className?: string;
    ariaLabel?: string;
}

/**
 * Horizontal, two-color-gradient funnel (see the design_html funnel
 * shoot-out — this is the FunnelGraph.js panel). Wraps the vendored
 * `FunnelGraph` class: draws on mount, redraws from scratch on any prop
 * change (the library has no cheap "just update the values" path worth
 * reusing here), and tracks container width with a ResizeObserver since the
 * library never measures itself.
 */
export const FunnelChart: React.FC<FunnelChartProps> = ({
    labels,
    values,
    colors,
    displayPercent = true,
    height = 220,
    className = '',
    ariaLabel = 'Funnel chart',
}) => {
    const hostRef = useRef<HTMLDivElement | null>(null);
    const resolvedColors = useMemo(() => colors ?? defaultGradient(), [colors]);

    useEffect(() => {
        const host = hostRef.current;
        if (!host) return undefined;

        host.innerHTML = '';

        const graph = new FunnelGraph({
            container: host,
            direction: 'horizontal',
            gradientDirection: 'horizontal',
            displayPercent,
            height,
            data: { labels, values, colors: resolvedColors },
        });
        graph.draw();

        let observer: ResizeObserver | undefined;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver((entries) => {
                const width = entries[0]?.contentRect.width;
                if (width) graph.updateWidth(width);
            });
            observer.observe(host);
        }

        return () => {
            observer?.disconnect();
            host.innerHTML = '';
        };
    }, [labels, values, resolvedColors, displayPercent, height]);

    return (
        <div
            ref={hostRef}
            className={className}
            style={{ width: '100%', height }}
            role="img"
            aria-label={ariaLabel}
        />
    );
};

export default FunnelChart;
